import React from "react";
import { cn } from "@/lib/utils";
import { initials } from "@/lib/helpers";

export function PageHeader({ title, subtitle, action, testid }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8" data-testid={testid}>
      <div>
        <h1 className="font-head text-3xl md:text-4xl font-bold tracking-tight text-black">{title}</h1>
        {subtitle && <p className="text-muted-foreground mt-1.5 text-base">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function StatCard({ icon: Icon, label, value, accent = "#2FB7B5", testid }) {
  return (
    <div
      data-testid={testid}
      className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-200"
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{label}</span>
        <span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: `${accent}1A` }}>
          {Icon && <Icon className="w-5 h-5" style={{ color: accent }} />}
        </span>
      </div>
      <div className="mt-3 font-head text-4xl font-bold text-black">{value}</div>
    </div>
  );
}

export function Avatar({ first, last, photo, size = 44, color = "#2FB7B5" }) {
  return (
    <div
      className="rounded-full flex items-center justify-center font-head font-bold text-white shrink-0 overflow-hidden"
      style={{ width: size, height: size, background: photo ? undefined : color, fontSize: size * 0.36 }}
    >
      {photo ? <img src={photo} alt="" className="w-full h-full object-cover" /> : initials(first, last)}
    </div>
  );
}

export function LevelBadge({ name, color = "#2FB7B5", small }) {
  if (!name) return <span className="text-sm text-muted-foreground">No level</span>;
  return (
    <span
      className={cn("inline-flex items-center gap-1.5 rounded-full font-bold uppercase tracking-wide",
        small ? "px-2.5 py-0.5 text-[11px]" : "px-3 py-1 text-xs")}
      style={{ background: `${color}1F`, color: color }}
    >
      <span className="w-2 h-2 rounded-full" style={{ background: color }} />
      {name}
    </span>
  );
}

export function ProgressBar({ value = 0, color = "#2FB7B5", height = 16 }) {
  return (
    <div className="w-full rounded-full bg-gray-200 overflow-hidden" style={{ height }}>
      <div
        className="h-full rounded-full transition-all duration-500 ease-out"
        style={{ width: `${Math.min(100, Math.max(0, value))}%`, background: color }}
      />
    </div>
  );
}

export function EmptyState({ icon: Icon, title, hint, testid }) {
  return (
    <div data-testid={testid} className="bg-white rounded-2xl border border-dashed border-gray-200 p-12 text-center">
      {Icon && <Icon className="w-10 h-10 mx-auto text-teal mb-3" />}
      <p className="font-head font-semibold text-lg text-black">{title}</p>
      {hint && <p className="text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}

export function RecitalCountdown({ name, date, location }) {
  if (!date) return null;
  const target = new Date(date + "T00:00:00");
  const days = Math.ceil((target - new Date()) / 86400000);
  const past = days < 0;
  const label = days > 1 ? `${days} days` : days === 1 ? "Tomorrow" : days === 0 ? "Today!" : "Wrapped 🎉";
  return (
    <div data-testid="recital-countdown" className="rounded-2xl p-6 text-white relative overflow-hidden"
      style={{ background: "linear-gradient(135deg,#0F766E 0%,#2FB7B5 60%,#5EEAD4 100%)" }}>
      <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full bg-white/10" />
      <div className="absolute right-10 bottom-2 w-16 h-16 rounded-full bg-white/10" />
      <p className="text-xs font-bold uppercase tracking-widest text-white/80">{past ? "Last Recital" : "Next Recital"}</p>
      <p className="font-head text-2xl font-extrabold mt-1">{name || "Upcoming Recital"}</p>
      <div className="flex items-end gap-3 mt-4">
        <span className="font-head text-5xl font-extrabold leading-none" data-testid="countdown-days">{label}</span>
        {days > 0 && <span className="text-white/80 text-sm mb-1">to go</span>}
      </div>
      <p className="text-sm text-white/85 mt-3">{target.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}{location ? ` · ${location}` : ""}</p>
    </div>
  );
}
